import {memo, useCallback, useMemo} from 'react'

import type {SortDirection} from '../api/view/contracts'
import {SortedByContext} from '../hooks/use-sorted-by'
import type {LocalSort} from '../hooks/use-view-state-reducer/types'
import {ViewStateActionTypes} from '../hooks/use-view-state-reducer/view-state-action-types'
import {useViews} from '../hooks/use-views'
import type {ColumnModel} from '../models/column-model'

export const nextSortDirection = (currentDirection: SortDirection | null): SortDirection | null => {
  switch (currentDirection) {
    case 'asc':
      return 'desc'
    case 'desc':
      return null
    case null:
      return 'asc'
  }
}

export const describeSortRanking = (index: number): string => {
  switch (index) {
    case 0:
      return 'primary'
    case 1:
      return 'secondary'
    default:
      return `no. ${index + 1}`
  }
}

const EMPTY = [] as const

export const SortedByProvider = memo<{
  children?: React.ReactNode
}>(function SortedByProvider({children}) {
  const {currentView, viewStateDispatch} = useViews()

  const viewNumber = currentView?.number

  const sorts = currentView?.localViewStateDeserialized.sortByColumnsAndDirections ?? EMPTY

  const setSortedBy = useCallback(
    (...newSorts: ReadonlyArray<LocalSort>): void => {
      if (viewNumber === undefined) return
      viewStateDispatch({type: ViewStateActionTypes.SetSortedBy, sorts: newSorts, viewNumber})
    },
    [viewNumber, viewStateDispatch],
  )

  const clearSortedBy = useCallback(
    (index = 0) => {
      if (viewNumber === undefined) return
      viewStateDispatch({type: ViewStateActionTypes.SetSortedBy, sorts: sorts.slice(0, index), viewNumber})
    },
    [viewNumber, viewStateDispatch, sorts],
  )

  const getColumnSort = useCallback((column: ColumnModel) => sorts.find(s => s.column.id === column.id), [sorts])

  const setColumnSort = useCallback(
    (column: ColumnModel, direction: SortDirection | null, index: number) => {
      const existingSortIndex = sorts.findIndex(s => s.column.id === column.id)

      if (existingSortIndex === index && direction !== null) {
        // If we're just changing the direction of an existing sort, preserve lower-priority sorts
        const updated = [...sorts]
        updated.splice(index, 1, {column, direction})
        setSortedBy(...updated)
      } else {
        // Otherwise, clear lower-priority sorts because we consider them to be like 'children' of higher priority sorts.

        // Using the smaller of the existing & target indexes ensures we delete any existing duplicates
        const resolvedIndex = existingSortIndex === -1 ? index : Math.min(existingSortIndex, index)

        const updated = sorts.slice(0, resolvedIndex)
        if (direction !== null) updated.push({column, direction})
        setSortedBy(...updated)
      }
    },
    [setSortedBy, sorts],
  )

  return (
    <SortedByContext.Provider
      value={useMemo(() => {
        return {
          sorts,
          isSorted: sorts.length > 0,
          setSortedBy,
          clearSortedBy,
          isSortedByDirty: currentView?.isSortedByDirty ?? false,
          setColumnSort,
          getColumnSort,
        }
      }, [clearSortedBy, currentView?.isSortedByDirty, getColumnSort, setColumnSort, setSortedBy, sorts])}
    >
      {children}
    </SortedByContext.Provider>
  )
})

try{ SortedByProvider.displayName ||= 'SortedByProvider' } catch {}